import dayjs from "dayjs";
import deburr from "lodash/deburr";
import orderBy from "lodash/orderBy";
import {
    Content,
    PageOrientation,
    PredefinedPageSize,
    TableCell,
    TDocumentDefinitions,
} from "pdfmake/interfaces";
import { defineMessages, useIntl } from "react-intl";
import { getReportClientName } from "../utils";
import { mainTableLayout, styles } from "./pdfStyles";
import { useReportFiltersPdf } from "./useReportFiltersPdf";
import { useReportPdf } from "./useReportPdf";
import { SalesReportData } from "./useSalesReportData";

const defaultPageSettings = {
    pageSize: "LETTER" as PredefinedPageSize,
    pageOrientation: "landscape" as PageOrientation,
    pageMargins: [30, 60, 30, 40] as [number, number, number, number],
};

const defaultStyle = {
    fontSize: 7.8,
};

const m = defineMessages({
    title: { id: "SalesReportPdf.Title", defaultMessage: "Sales by client report" },
    onOrder: { id: "SalesReportPdf.OnOrder", defaultMessage: "On order" },
    billed: { id: "SalesReportPdf.Billed", defaultMessage: "Billed" },
    client: { id: "SalesReportPdf.Client", defaultMessage: "Client" },
    totals: { id: "SalesReportPdf.Totals", defaultMessage: "Totals" },
});

export const useSalesReportPdf = (reportData: SalesReportData) => {
    const { formatMessage } = useIntl();

    const { getFormattedAmount, getFormattedDate, getFormattedRatio } = useReportPdf();
    const { getActiveFilterTable } = useReportFiltersPdf(reportData.filters);

    const clientReportHeader = (currentPage: number): Content => {
        return {
            margin: [30, 20, 30, 0],
            columns: [
                { text: `Date ${getFormattedDate(new Date())}` },
                {
                    width: "*",
                    style: "documentTitle",
                    text: formatMessage(m.title).toUpperCase(),
                },
                {
                    alignment: "right",
                    text: `Page ${currentPage}`,
                },
            ],
        };
    };

    const getTableHeaders = (): TableCell[] => {
        return [
            { text: formatMessage(m.client).toUpperCase() },
            { text: "" },
            ...reportData.monthsInOrder.map((month) => ({
                text: dayjs().month(month).format("MMM").toUpperCase(),
                style: "monthHeader",
            })),
            { text: formatMessage(m.totals).toUpperCase(), style: "totalHeader" },
        ];
    };

    const getCurrentPeriodRow = (clientCode: string): TableCell[] => {
        const monthlySales = reportData.monthlySalesByClientCode[clientCode];
        const incomingBillingAmount = reportData.incomingBillingAmountByClientCode[clientCode] || 0;

        return [
            { text: clientCode },
            {
                columns: [
                    { text: formatMessage(m.onOrder) },
                    {
                        text: getFormattedAmount(incomingBillingAmount),
                        alignment: "right",
                    },
                ],
            },
            ...reportData.monthsInOrder.map((month) => ({
                text: getFormattedAmount(monthlySales?.statsByMonth[month]?.currentPeriodTotal),
                style: "periodAmount",
            })),
            { text: getFormattedAmount(monthlySales?.currentPeriodTotal), style: "totalAmount" },
        ];
    };

    const getPreviousPeriodRow = (clientCode: string): TableCell[] => {
        const monthlySales = reportData.monthlySalesByClientCode[clientCode];
        const billedAmountAfterEndDate =
            reportData.billedAmountAfterEndDateByClientCode[clientCode] || 0;

        return [
            { text: "" },
            {
                columns: [
                    { text: formatMessage(m.billed) },
                    {
                        text: getFormattedAmount(billedAmountAfterEndDate),
                        alignment: "right",
                    },
                ],
            },
            ...reportData.monthsInOrder.map((month) => ({
                text: getFormattedAmount(monthlySales?.statsByMonth[month]?.previousPeriodTotal),
                style: "periodAmount",
            })),
            { text: getFormattedAmount(monthlySales?.previousPeriodTotal), style: "totalAmount" },
        ];
    };

    const getRatiosRow = (clientCode: string): TableCell[] => {
        const clientName = getReportClientName(
            reportData.clientInformationByClientCode,
            clientCode,
        );
        const monthlySales = reportData.monthlySalesByClientCode[clientCode];

        return [
            { text: clientName },
            { text: "" },
            ...reportData.monthsInOrder.map((month) => ({
                text: getFormattedRatio(
                    monthlySales?.statsByMonth[month]?.ratio,
                    monthlySales?.statsByMonth[month]?.previousPeriodTotal,
                    monthlySales?.statsByMonth[month]?.currentPeriodTotal,
                ),
                style: "periodRatio",
            })),
            {
                text: getFormattedRatio(
                    monthlySales?.ratio,
                    monthlySales?.previousPeriodTotal,
                    monthlySales?.currentPeriodTotal,
                ),
                style: "totalRatio",
            },
        ];
    };

    const getClientRows = (clientCode: string): TableCell[][] => {
        return [
            getCurrentPeriodRow(clientCode),
            getPreviousPeriodRow(clientCode),
            getRatiosRow(clientCode),
        ];
    };

    const getTableRows = (): TableCell[][] => {
        const orderedClientCodes = orderBy(reportData.clientCodes, (clientCode) =>
            deburr(
                getReportClientName(
                    reportData.clientInformationByClientCode,
                    clientCode,
                ).toLowerCase(),
            ),
        );

        return orderedClientCodes.flatMap((clientCode) => getClientRows(clientCode));
    };

    const getMainTable = (): Content => {
        return {
            table: {
                headerRows: 1,
                widths: [
                    "*",
                    "auto",
                    "auto",
                    "auto",
                    "auto",
                    "auto",
                    "auto",
                    "auto",
                    "auto",
                    "auto",
                    "auto",
                    "auto",
                    "auto",
                    "auto",
                    "auto",
                ],
                body: [getTableHeaders(), ...getTableRows()],
            },
            layout: mainTableLayout,
        };
    };

    const getDocumentDefinition = (): TDocumentDefinitions => {
        return {
            ...defaultPageSettings,
            header: clientReportHeader,
            styles: styles,
            defaultStyle: defaultStyle,
            content: [getActiveFilterTable(), getMainTable()],
        };
    };

    return {
        getDocumentDefinition,
    };
};
